/**
 * WatchlistPillContainer — binds the pill's watchlist id into its press handler.
 *
 * The pill used to be a second component inside `PlayerIdentityBlockView`,
 * holding a `useCallback` there (SE-8019). It has two callers — the desktop
 * indicator and the mobile row — so a shared directory is where it belonged.
 * @module features/player-detail/components/molecules/WatchlistPill/WatchlistPillContainer
 */

import React, { memo, useCallback } from "react";

import WatchlistPillView from "./WatchlistPillView";

/** Props for {@link WatchlistPillContainer}. */
export interface WatchlistPillContainerProps {
  /** Watchlist id, used for the testID and the press payload. */
  readonly id: string;
  /** Watchlist display name. */
  readonly name: string;
  /** Tap handler; when omitted the pill is non-interactive. */
  readonly onPress?: (watchlistId: string) => void;
}

/**
 * Binds the pill's identity and renders it.
 * @param root0 - Props
 * @param root0.id - Watchlist id
 * @param root0.name - Watchlist display name
 * @param root0.onPress - Tap handler; omitted means non-interactive
 * @returns The pill.
 */
const WatchlistPillContainer = ({
  id,
  name,
  onPress,
}: WatchlistPillContainerProps) => {
  const handlePress = useCallback(() => onPress?.(id), [onPress, id]);

  return (
    <WatchlistPillView
      id={id}
      name={name}
      onPress={onPress === undefined ? null : handlePress}
    />
  );
};

WatchlistPillContainer.displayName = "WatchlistPillContainer";

export default memo(WatchlistPillContainer);
